import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { createBrowserRouter, Navigate, useParams, Outlet } from 'react-router-dom';

import { ProviderReferenceWrapper } from '../../v2/js/pages/Endorsements/ProviderReference/ProviderReferenceWrapper';
import { ProviderSurveyWrapper } from '../../v2/js/pages/Endorsements/ProviderSurvey/ProviderSurveyWrapper';
import { ProviderOnboardingWrapper } from '../../v2/js/pages/Onboarding/Provider/ProviderOnboardingWrapper';
import { NeedForm, NeedsList } from '../../v2/js/pages/Organization/Needs';
import OpportunityForm from '../../v2/js/pages/Organization/Opportunities/OpportunityForm';
import OpportunityList from '../../v2/js/pages/Organization/Opportunities/OpportunityList';
import { ProviderAvailabilityWrapper } from '../../v2/js/pages/Provider/ProviderAvailability';
import { ProviderFacilityDetailsFull } from '../../v2/js/pages/Provider/ProviderFacilityDetailsFull';
import { ProviderHomeWrapper } from '../../v2/js/pages/Provider/ProviderHome/ProviderHomeWrapper';
import { StaffHome } from '../../v2/js/pages/Staff';
import { RegistrationStatusEnum, UserTypeEnum } from '../api';
import { App } from '../App';
import { BookingRequestList } from '../pages/BookingRequestList';
import { ConnectionRequestDetails } from '../pages/ConnectionRequestDetails';
import { ConnectionRequestsList } from '../pages/ConnectionRequestsList';
import { DocumentsPage } from '../pages/DocumentsPage';
import { EntityProfile } from '../pages/EntityProfile';
import { FacilityRatingPage } from '../pages/FacilityRatingPage';
import { ForgotPassword } from '../pages/ForgotPassword';
import { HealthcareEntityDashboard } from '../pages/HealthcareEntityDashboard';
import { FacilityCreate } from '../pages/HealthcareEntityDashboard/FacilityCreate';
import { FacilitiesList } from '../pages/HealthcareEntityDashboard/HealthcareEntityFacilitiesList';
import { HealthcareEntityPendingInvitationsList } from '../pages/HealthcareEntityDashboard/HealthcareEntityPendingInvitationsList/HealthcareEntityPendingInvitationsList';
import { HealthcareEntityProviderList } from '../pages/HealthcareEntityDashboard/HealthcareEntityProviderList';
import { HealthcareEntityUsersList } from '../pages/HealthcareEntityDashboard/HealthcareEntityUsersList';
import { ProviderProfile } from '../pages/HealthcareEntityDashboard/ProviderProfile';
import { Home } from '../pages/Home';
import { HomeRedirect } from '../pages/HomeRedirect';
import { InvitationRegisterPage } from '../pages/InvitationRegisterPage';
import { LeaderRatingPage } from '../pages/LeaderRatingPage';
import { Login } from '../pages/Login';
import { NotFound } from '../pages/NotFound';
import { PasswordReset } from '../pages/PasswordReset';
import { PeerRatingPage } from '../pages/PeerRatingPage';
import { CurrentProfileEdit } from '../pages/ProviderDashboard/CurrentProviderEdit';
import { ProviderEducation } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderEducation';
import { ProviderIdentification } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderIdentification';
import { ProviderLicenses } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderLicenses';
import { ProviderOtherInfo } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderOtherInfo';
import { ProviderPersonalInfo } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderPersonalInfo';
import { ProviderProfessionalTraits } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderProfessionalTraits';
import { ProviderRates } from '../pages/ProviderDashboard/CurrentProviderEdit/ProviderRates';
import { CurrentProviderProfile } from '../pages/ProviderDashboard/CurrentProviderProfile';
import { Credentials } from '../pages/ProviderDashboard/CurrentProviderProfile/Credentials';
import { Education } from '../pages/ProviderDashboard/CurrentProviderProfile/Education';
import { Licenses } from '../pages/ProviderDashboard/CurrentProviderProfile/Licenses';
import { Other } from '../pages/ProviderDashboard/CurrentProviderProfile/Other';
import { ProfessionalTraits } from '../pages/ProviderDashboard/CurrentProviderProfile/ProfessionalTraits';
import { Reputation } from '../pages/ProviderDashboard/CurrentProviderProfile/Reputation';
import { ProviderSchedule } from '../pages/ProviderDashboard/ProviderSchedule';
import { ProviderQuestionnaire } from '../pages/ProviderQuestionnaire';
import { ProvisionalStatusGateway } from '../pages/ProvisionalStatusGateway';
import { RatingThankYouPage } from '../pages/RatingThankYouPage';
import { AccountSetup } from '../pages/Registration/AccountSetup';
import { EmailConfirmation } from '../pages/Registration/PendingEmailConfirmation';
import { SocialSignup } from '../pages/Registration/SocialSignup';
import { SocialLogin } from '../pages/SocialLogin';
import { SwagRedirect } from '../pages/Swag/SwagRedirect';
import { VerifyEmail } from '../pages/VerifyEmail';
import { ALL_USER_TYPES, AuthenticatedRoute } from './AuthenticatedRoute';

const registerRoutes = [
  { path: '/register/complete', element: <SocialSignup /> },
  {
    element: (
      <GoogleReCaptchaProvider reCaptchaKey={window.RECAPTCHA_SITE_KEY}>
        <Outlet />
      </GoogleReCaptchaProvider>
    ),
    children: [
      {
        path: '/register/provider/setup',
        element: (
          <AccountSetup userType={UserTypeEnum.PROVIDER} documentTitle="Provider Account Setup" />
        ),
      },
      {
        path: '/register/rep/setup',
        element: (
          <AccountSetup
            userType={UserTypeEnum.HEALTHCARE_ENTITY_REP}
            documentTitle="Healthcare Organization Account Setup"
          />
        ),
      },
      { path: '/register/email-confirmation', element: <EmailConfirmation /> },
    ],
  },
];

// Redirect components for facility routes - redirect to facilities list with drawer
function FacilityRedirect() {
  const { facilityId } = useParams();
  return <Navigate to={`/app/healthcare-entity/facilities?id=${facilityId}`} replace />;
}

// Redirect components for endorsement routes
function EndorsementRedirect() {
  return <Navigate to="/endorsement/provider-reference" replace />;
}

function EndorsementIdRedirect() {
  const { endorsementId } = useParams();
  return <Navigate to={`/endorsement/provider-reference/${endorsementId}`} replace />;
}

// Redirect opportunity view route to opportunity list with drawer
function OpportunityViewRedirect() {
  const { opportunityId } = useParams();
  return <Navigate to={`/app/organization/opportunities?id=${opportunityId}`} replace />;
}

export const routes: ReturnType<typeof createBrowserRouter> = createBrowserRouter([
  {
    element: <App />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/home', element: <Home /> },
      { path: '/login', element: <Login /> },
      { path: '/social-login/:socialApp', element: <SocialLogin /> },
      { path: '/invitation/:invitationId', element: <InvitationRegisterPage /> },
      { path: '/verify-email/:key', element: <VerifyEmail /> },
      { path: '/forgot-password', element: <ForgotPassword /> },
      { path: '/password-reset/:uid/:token', element: <PasswordReset /> },
      { path: '/swag', element: <SwagRedirect /> },
      // Old endorsement routes - redirect to new paths
      // can be deprecated once no more /endorsement routes are in use
      { path: '/endorsement', element: <EndorsementRedirect /> },
      { path: '/endorsement/:endorsementId', element: <EndorsementIdRedirect /> },
      { path: '/endorsement/provider-reference', element: <ProviderReferenceWrapper /> },
      { path: '/endorsement/provider-reference/:id', element: <ProviderReferenceWrapper /> },
      { path: '/endorsement/provider-survey', element: <ProviderSurveyWrapper /> },
      { path: '/endorsement/provider-survey/:id', element: <ProviderSurveyWrapper /> },
      ...registerRoutes,
      {
        path: '/register/provisional',
        element: (
          <AuthenticatedRoute
            acceptedStatuses={[RegistrationStatusEnum.MISSING_INFO]}
            allowedUserTypes={[UserTypeEnum.HEALTHCARE_ENTITY_REP]}
          />
        ),
        children: [
          {
            path: '/register/provisional/healthcare-entity-rep',
            element: <ProvisionalStatusGateway userType={UserTypeEnum.HEALTHCARE_ENTITY_REP} />,
          },
        ],
      },
      {
        path: '/staff',
        element: <AuthenticatedRoute allowedUserTypes={[UserTypeEnum.STAFF]} />,
        children: [{ path: '/staff/home', element: <StaffHome /> }],
      },
      {
        path: '/pub',
        element: <Outlet />,
        children: [
          {
            path: '/pub/provider/availability/:token',
            element: <ProviderAvailabilityWrapper />,
          },
        ],
      },
      {
        path: '/app',
        element: (
          <AuthenticatedRoute
            acceptedStatuses={[
              RegistrationStatusEnum.PROVISIONAL,
              RegistrationStatusEnum.COMPLETE,
              RegistrationStatusEnum.PROVIDER_PROFILE,
            ]}
            allowedUserTypes={ALL_USER_TYPES}
          />
        ),
        children: [
          { path: '/app', element: <HomeRedirect /> },
          {
            path: '/app/provider',
            element: <AuthenticatedRoute allowedUserTypes={[UserTypeEnum.PROVIDER]} />,
            children: [
              { path: '/app/provider/', element: <HomeRedirect /> },
              { path: '/app/provider/onboarding', element: <ProviderOnboardingWrapper /> },
              { path: '/app/provider/home', element: <ProviderHomeWrapper /> },
              { path: '/app/provider/documents', element: <DocumentsPage /> },
              {
                path: '/app/provider/facility-details/:facilityId',
                element: <ProviderFacilityDetailsFull />,
              },
              {
                path: '/app/provider/profile',
                element: <CurrentProviderProfile />,
                children: [
                  { path: '/app/provider/profile/', element: <ProviderSchedule /> },
                  { path: '/app/provider/profile/credentials', element: <Credentials /> },
                  {
                    path: '/app/provider/profile/professional-traits',
                    element: <ProfessionalTraits />,
                  },
                  { path: '/app/provider/profile/education', element: <Education /> },
                  { path: '/app/provider/profile/licenses', element: <Licenses /> },
                  { path: '/app/provider/profile/reputation', element: <Reputation /> },
                  { path: '/app/provider/profile/other', element: <Other /> },
                ],
              },
              {
                path: '/app/provider/edit',
                element: <CurrentProfileEdit />,
                children: [
                  { path: '/app/provider/edit', element: <ProviderPersonalInfo /> },
                  {
                    path: '/app/provider/edit/rates',
                    element: <ProviderRates />,
                  },
                  { path: '/app/provider/edit/education', element: <ProviderEducation /> },
                  { path: '/app/provider/edit/licenses', element: <ProviderLicenses /> },
                  { path: '/app/provider/edit/other', element: <ProviderOtherInfo /> },
                  {
                    path: '/app/provider/edit/professional-traits',
                    element: <ProviderProfessionalTraits />,
                  },
                  {
                    path: '/app/provider/edit/identification',
                    element: <ProviderIdentification />,
                  },
                ],
              },
              { path: '/app/provider/connection-requests', element: <ConnectionRequestsList /> },
              {
                path: '/app/provider/connection-requests/:id',
                element: <ConnectionRequestDetails />,
              },
              { path: '/app/provider/booking-requests/', element: <BookingRequestList /> },
              {
                path: '/app/provider/booking-requests/:bookingId',
                element: <BookingRequestList />,
              },
              {
                path: '/app/provider/facility-rating/:facilityId',
                element: <FacilityRatingPage />,
              },
              {
                path: '/app/provider/questionnaire/:providerId',
                element: <ProviderQuestionnaire />,
              },
            ],
          },
          {
            path: '/app/organization/',
            element: (
              <AuthenticatedRoute
                acceptedStatuses={[RegistrationStatusEnum.COMPLETE]}
                allowedUserTypes={[UserTypeEnum.HEALTHCARE_ENTITY_REP, UserTypeEnum.STAFF]}
              />
            ),
            children: [
              {
                path: '/app/organization/',
                element: <HealthcareEntityDashboard />,
                children: [
                  {
                    path: '/app/organization/opportunities/new',
                    element: <OpportunityForm />,
                  },
                  {
                    path: '/app/organization/opportunities/:opportunityId',
                    element: <OpportunityViewRedirect />,
                  },
                  {
                    path: '/app/organization/opportunities/:opportunityId/edit',
                    element: <OpportunityForm />,
                  },
                  {
                    path: '/app/organization/opportunities',
                    element: <OpportunityList />,
                  },
                  {
                    path: '/app/organization/needs/new',
                    element: <NeedForm />,
                  },
                  // {
                  //   path: '/app/organization/needs/:needId',
                  //   element: <NeedForm />,
                  // },
                  {
                    path: '/app/organization/needs/:needId/edit',
                    element: <NeedForm />,
                  },
                  {
                    path: '/app/organization/needs',
                    element: <NeedsList />,
                  },
                  // DEPRECATE: remove this once frontend/js/pages directory is fully replaced
                  {
                    path: '/app/organization/facilities/:facilityId',
                    element: <FacilityRedirect />,
                  },
                ],
              },
            ],
          },
          {
            path: '/app/healthcare-entity/',
            element: (
              <AuthenticatedRoute
                acceptedStatuses={[RegistrationStatusEnum.COMPLETE]}
                allowedUserTypes={[UserTypeEnum.HEALTHCARE_ENTITY_REP, UserTypeEnum.STAFF]}
              />
            ),
            children: [
              {
                path: '/app/healthcare-entity/',
                element: <HealthcareEntityDashboard />,
                children: [
                  { path: '/app/healthcare-entity/', element: <HomeRedirect /> },
                  {
                    path: '/app/healthcare-entity/providers',
                    element: <HealthcareEntityProviderList />,
                  },
                  {
                    path: '/app/healthcare-entity/providers/:providerId',
                    element: <ProviderProfile />,
                    children: [
                      {
                        path: '/app/healthcare-entity/providers/:providerId/',
                        element: <ProviderSchedule isBooking />,
                      },
                      {
                        path: '/app/healthcare-entity/providers/:providerId/credentials',
                        element: <Credentials />,
                      },
                      {
                        path: '/app/healthcare-entity/providers/:providerId/professional-traits',
                        element: <ProfessionalTraits />,
                      },
                      {
                        path: '/app/healthcare-entity/providers/:providerId/education',
                        element: <Education />,
                      },
                      {
                        path: '/app/healthcare-entity/providers/:providerId/licenses',
                        element: <Licenses />,
                      },
                      {
                        path: '/app/healthcare-entity/providers/:providerId/other',
                        element: <Other />,
                      },
                    ],
                  },
                  {
                    path: '/app/healthcare-entity/providers/:providerId/questionnaire/',
                    element: <ProviderQuestionnaire />,
                  },
                  {
                    path: '/app/healthcare-entity/connection-requests',
                    element: <ConnectionRequestsList />,
                  },
                  {
                    path: '/app/healthcare-entity/booking-requests/',
                    element: <BookingRequestList />,
                  },
                  {
                    path: '/app/healthcare-entity/booking-requests/:bookingId',
                    element: <BookingRequestList />,
                  },
                  {
                    path: '/app/healthcare-entity/facilities',
                    element: <FacilitiesList showAddFacilityButton={false} />,
                  },
                  { path: '/app/healthcare-entity/facilities/new', element: <FacilityCreate /> },
                  {
                    path: '/app/healthcare-entity/facilities/:facilityId/edit',
                    element: <FacilityRedirect />,
                  },
                  { path: '/app/healthcare-entity/users', element: <HealthcareEntityUsersList /> },
                  {
                    path: '/app/healthcare-entity/pending-invitations',
                    element: <HealthcareEntityPendingInvitationsList />,
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: '/app/healthcare-entity/:entityId', element: <EntityProfile /> },
      {
        path: '/app/healthcare-entity/facilities/:facilityId',
        element: <FacilityRedirect />,
      },
      {
        path: '/app/peer-rating/:providerId',
        element: (
          <GoogleReCaptchaProvider reCaptchaKey={window.RECAPTCHA_SITE_KEY}>
            <PeerRatingPage />
          </GoogleReCaptchaProvider>
        ),
      },
      {
        path: '/app/leader-rating/:token',
        element: (
          <GoogleReCaptchaProvider reCaptchaKey={window.RECAPTCHA_SITE_KEY}>
            <LeaderRatingPage />
          </GoogleReCaptchaProvider>
        ),
      },
      { path: '/app/rating-thank-you', element: <RatingThankYouPage /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
